import { Component, ErrorInfo, ReactNode } from "react";
import { logError } from "@/lib/errorHandler";
import { ErrorFallback } from "./ErrorFallback";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

const isStaleChunkError = (error: unknown): boolean => {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    msg.includes("Importing a module script failed") ||
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("ChunkLoadError") ||
    msg.includes("Loading chunk")
  );
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Chunks viejos post-deploy: recargar una vez para tomar el bundle nuevo.
    // sessionStorage marker evita loop infinito si la recarga no resuelve.
    if (isStaleChunkError(error)) {
      const reloaded = sessionStorage.getItem("ranerzzz_chunk_reload");
      if (!reloaded) {
        sessionStorage.setItem("ranerzzz_chunk_reload", "1");
        window.location.reload();
        return;
      }
    }
    // Forward al logger con contexto del componente. El error real va como
    // primer argumento (no envuelto en object) para que toError() lo respete.
    logError("error-boundary", error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}
